import { ChangeDetectorRef, Component, inject, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalRef } from '@siemens/element-ng/modal';
import { AskAiService, AskAiResponse } from 'src/app/services/ask-ai.service';
import { ConfigService } from 'src/app/services/config.service';
import { finalize, take, timeout } from 'rxjs/operators';

@Component({
  selector: 'app-ask-ai-dialog',
  templateUrl: './ask-ai-dialog.component.html',
  styleUrl: './ask-ai-dialog.component.scss',
  standalone: false,
})
export class AskAiDialogComponent {
  question: string = '';
  response: AskAiResponse | null = null;
  responseText: string = '';
  dataSourcesUsed: string[] = [];
  isLoading: boolean = false;
  error: string | null = null;
    detailsExpanded: boolean = false;

  private modalRef = inject(ModalRef);
  private askAiService = inject(AskAiService);
  private configService = inject(ConfigService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  private readonly htmlEntityParser = typeof document !== 'undefined'
    ? document.createElement('textarea')
    : null;

  private normalizeResponse(payload: unknown): AskAiResponse {
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload) as AskAiResponse;
      } catch {
        return { result: payload, response: payload } as AskAiResponse;
      }
    }

    if (payload && typeof payload === 'object' && 'body' in (payload as Record<string, unknown>)) {
      const wrappedBody = (payload as { body?: unknown }).body;
      if (wrappedBody && typeof wrappedBody === 'object') {
        return wrappedBody as AskAiResponse;
      }
      if (typeof wrappedBody === 'string') {
        try {
          return JSON.parse(wrappedBody) as AskAiResponse;
        } catch {
          return { result: wrappedBody, response: wrappedBody } as AskAiResponse;
        }
      }
    }

    return (payload ?? {}) as AskAiResponse;
  }

  onSubmit(): void {
    if (!this.question.trim()) {
      return;
    }

    this.isLoading = true;
    this.response = null;
    this.responseText = '';
    this.dataSourcesUsed = [];
    this.error = null;
      this.detailsExpanded = false;
    this.cdr.markForCheck();

    const wizRoles = this.configService.currentUser?.wizRoles ?? [];
    const queryWithRoleContext = wizRoles.length > 0
      ? `[WIZ Roles: ${wizRoles.join(', ')}]\n${this.question}`
      : this.question;

    this.askAiService.askAiAndGetAnswer(queryWithRoleContext)
      .pipe(take(1), timeout(210000))
      .pipe(finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
      next: (rawResponse) => {
        this.isLoading = false;
        const response = this.normalizeResponse(rawResponse);
        this.response = response;
        this.responseText = this.normalizeDisplayText(response.response ?? response.result ?? '');
        this.dataSourcesUsed = Array.isArray(response.dataSourcesUsed) ? response.dataSourcesUsed : [];

        if (response.success === false && !this.responseText) {
          this.error = 'AskAI returned an unsuccessful response.';
        }

        if (!this.responseText && !this.error) {
          this.error = 'No response text was returned from AskAI.';
        }

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        const timeoutMessage = error?.name === 'TimeoutError' ? 'Request timed out. Please try again.' : null;
        this.error = timeoutMessage ?? `Error: ${error.error?.message || error.error?.response || error.message || 'Failed to get response'}`;
        this.cdr.markForCheck();
      }
    });
  }

  onCancel(): void {
    this.modalRef.hide();
  }

  get parsedResponseParts(): { before: string; details: string; after: string } | null {
    const parts = this.responseText.split(/\n?---\n?/);
    if (parts.length >= 3) {
      const before = parts[0].trim();
      const details = parts.slice(1, parts.length - 1).join('\n---\n').trim();
      const after = parts[parts.length - 1].trim();

      // Ignore low-value wrappers like "--- details ---" that add noise.
      if (!details || /^details$/i.test(details)) {
        return null;
      }

      return {
        before,
        details,
        after
      };
    }

    return null;
  }

  renderResponseHtml(content: string): string {
    if (!content) {
      return '';
    }

    const html = this.buildSafeHtml(content);
    return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
  }

  private buildSafeHtml(content: string): string {
    const lines = content.split('\n');
    const result: string[] = [];
    let inList = false;

    for (const line of lines) {
      const trimmed = line.trimEnd();

      if (!trimmed) {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        continue;
      }

      const listMatch = trimmed.match(/^\s*[-*]\s+(.*)/);
      if (listMatch) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${this.applyInlineFormatting(this.escapeHtml(listMatch[1]))}</li>`);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(`<p>${this.applyInlineFormatting(this.escapeHtml(trimmed))}</p>`);
      }
    }

    if (inList) {
      result.push('</ul>');
    }

    return result.join('');
  }

  private applyInlineFormatting(escaped: string): string {
    return escaped
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private normalizeDisplayText(content: string): string {
    if (!content) {
      return '';
    }

    const decoded = this.decodeHtmlEntities(content);
    return decoded
      .replace(/\r\n/g, '\n')
      .replace(/^---[a-zA-Z]*---\n?/, '')
      .trim();
  }

  private decodeHtmlEntities(content: string): string {
    if (!this.htmlEntityParser) {
      return content;
    }

    this.htmlEntityParser.innerHTML = content;
    return this.htmlEntityParser.value;
  }
}
